/**
 * The firm's desks and the desk phones on them: the list, adding either, the order, and
 * what to type into a phone.
 *
 * Actions rather than settings, so each one saves when it is pressed and the page's save
 * bar does not count them. Adding a phone shows its password once — it is written to the
 * PBX and kept nowhere else, like a browser telephone's — so the dialog that shows it says
 * so and stays until it is closed. Losing it costs a reset, not a support call.
 *
 * A desk is drawn as a row with its phones beneath it, in the desk's order. The desks' own
 * order is the order calls reach them in, at a firm that shares calls out in turns or in
 * order, so it is moved with arrows rather than left to the order desks were added in.
 * Adding a desk adds its first phone with it — a desk with no phone rings nothing.
 *
 * Where phones belong to dispatchers the administrator gives each desk to somebody here;
 * where they stay on their desks, the list says who is sitting at each.
 *
 * A phone can also set itself up: record the MAC from its label, type the one address shown
 * into its Auto Provision page, and it downloads everything else — including a password
 * minted for every download, so nothing is ever typed but that address.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { describe } from '../errors';
import { staffApi, type StaffMember } from '../fleet/staff';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  KeyValue,
  KeyValueRow,
  Pill,
  Section,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../../ui';
import type { DeskPhoneOwnership } from '../../telephony/api';
import { deskPhonesApi, type Desk, type DeskPhone, type DeskPhoneCredentials } from './deskPhones';

/** What the PBX says the handset's own settings page should get, when it has a MAC. */
function openSetup(
  phone: DeskPhone,
  setSetup: (phone: DeskPhone) => void,
  setMac: (mac: string) => void,
  setAddress: (url: string | null) => void,
) {
  setSetup(phone);
  setMac(phone.macAddress === null ? '' : pairs(phone.macAddress));
  setAddress(null);

  if (phone.macAddress !== null) {
    void deskPhonesApi
      .provisioning()
      .then((answer) => setAddress(answer.url))
      .catch(() => setAddress(null));
  }
}

/** `805ec0123456` as the label prints it: `80:5E:C0:12:34:56`. */
function pairs(mac: string): string {
  return (mac.match(/.{2}/g) ?? []).join(':').toUpperCase();
}

export function DeskPhonesSection({ belongTo }: { belongTo: DeskPhoneOwnership }) {
  const { t } = useTranslation();

  const [desks, setDesks] = useState<Desk[] | null>(null);

  /** The name box, focused when the dialog opens — the native dialog would focus its Close. */
  const nameBox = useRef<HTMLInputElement>(null);
  const [staff, setStaff] = useState<StaffMember[]>([]);

  /** What the name dialog is for: a new desk, a phone on a desk, or renaming a desk. */
  const [naming, setNaming] = useState<Naming | null>(null);
  const [name, setName] = useState('');
  const [shown, setShown] = useState<DeskPhoneCredentials | null>(null);
  const [removing, setRemoving] = useState<DeskPhone | null>(null);
  const [removingDesk, setRemovingDesk] = useState<Desk | null>(null);

  /** The phone whose self-setup is open, what was typed as its MAC, and its address. */
  const [setup, setSetup] = useState<DeskPhone | null>(null);
  const [mac, setMac] = useState('');
  const [address, setAddress] = useState<string | null>(null);
  const [synced, setSynced] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDesks(await deskPhonesApi.desks());
    } catch (failure) {
      setError(describe(failure, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  useEffect(() => {
    if (naming === null) return;

    // After showModal has done its own focusing, which it does on the same tick.
    const frame = requestAnimationFrame(() => nameBox.current?.focus());

    return () => cancelAnimationFrame(frame);
  }, [naming]);

  // The people a desk can be given to, only where desks are given at all.
  useEffect(() => {
    if (belongTo !== 'Dispatcher') return;

    void staffApi
      .list()
      .then(setStaff)
      .catch(() => setStaff([]));
  }, [belongTo]);

  async function act(action: () => Promise<void>) {
    setBusy(true);
    setError(null);

    try {
      await action();
    } catch (failure) {
      setError(describe(failure, t, 'settings.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  function open(next: Naming) {
    setNaming(next);
    setName(
      next.kind === 'phone'
        ? `${next.desk.name} · ${next.desk.phones.length + 1}`
        : next.kind === 'rename'
          ? next.desk.name
          : '',
    );
  }

  // No <form> of its own: this section sits inside the page's, and a form cannot hold one.
  function submitName() {
    if (naming === null || name.trim() === '') return;

    const trimmed = name.trim();

    void act(async () => {
      if (naming.kind === 'rename') {
        await deskPhonesApi.renameDesk(naming.desk.id, trimmed);
      } else {
        // A new desk is added with its first phone, which is what a desk with no phone
        // was for anyway; a phone on a desk goes last on it.
        setShown(
          await deskPhonesApi.add(trimmed, naming.kind === 'phone' ? naming.desk.id : null),
        );
      }

      setNaming(null);
      setName('');
      await load();
    });
  }

  /** Moves one item of a list a place up or down, and sends the whole new order. */
  function swap<T extends { id: string }>(
    list: T[],
    index: number,
    by: -1 | 1,
    send: (ids: string[]) => Promise<void>,
  ) {
    const ids = list.map((item) => item.id);
    const other = index + by;

    if (other < 0 || other >= ids.length) return;

    [ids[index], ids[other]] = [ids[other]!, ids[index]!];

    void act(async () => {
      await send(ids);
      await load();
    });
  }

  return (
    <Section
      title={t('deskPhones.title')}
      description={t('deskPhones.help')}
      actions={
        <Button size="sm" disabled={busy} onClick={() => open({ kind: 'desk' })}>
          <Icon name="plus" small />
          {t('deskPhones.addDesk')}
        </Button>
      }
    >
      {error !== null && (
        <div className="px-5 pt-3">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      {desks === null ? (
        error === null && <EmptyState>{t('app.loading')}</EmptyState>
      ) : desks.length === 0 ? (
        <EmptyState>{t('deskPhones.none')}</EmptyState>
      ) : (
        <Table>
          <thead>
            <Tr>
              <Th>{t('deskPhones.name')}</Th>
              <Th>{t('deskPhones.extension')}</Th>
              <Th>{belongTo === 'Dispatcher' ? t('deskPhones.givenTo') : t('deskPhones.atIt')}</Th>
              <Th>{t('deskPhones.state')}</Th>
              <Th />
            </Tr>
          </thead>
          {desks.map((desk, deskIndex) => (
            <tbody key={desk.id}>
              <Tr>
                <Td>
                  <span className="font-semibold">
                    <span className="tabular-nums text-ink-3">{desk.position}.</span> {desk.name}
                  </span>
                </Td>
                <Td />
                <Td wrap>
                  {belongTo === 'Dispatcher' ? (
                    <Select
                      aria-label={t('deskPhones.givenTo')}
                      value={desk.assignedUserId ?? ''}
                      disabled={busy}
                      onChange={(e) =>
                        void act(async () => {
                          await deskPhonesApi.assign(
                            desk.id,
                            e.target.value === '' ? null : e.target.value,
                          );
                          await load();
                        })
                      }
                    >
                      <option value="">{t('deskPhones.nobody')}</option>
                      {staff.map((member) => (
                        <option key={member.userId} value={member.userId}>
                          {member.displayName ?? member.userId}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    (desk.occupantName ?? <span className="text-ink-3">{t('deskPhones.nobody')}</span>)
                  )}
                </Td>
                <Td />
                <Td>
                  <span className="flex justify-end gap-1.5">
                    <Order
                      index={deskIndex}
                      count={desks.length}
                      disabled={busy}
                      onMove={(by) => swap(desks, deskIndex, by, deskPhonesApi.reorderDesks)}
                    />
                    <Button size="sm" disabled={busy} onClick={() => open({ kind: 'phone', desk })}>
                      <Icon name="plus" small />
                      {t('deskPhones.addPhone')}
                    </Button>
                    <Button
                      size="sm"
                      variant="quiet"
                      aria-label={t('deskPhones.renameDesk')}
                      title={t('deskPhones.renameDesk')}
                      disabled={busy}
                      onClick={() => open({ kind: 'rename', desk })}
                    >
                      <Icon name="pencil" small />
                    </Button>
                    {desk.phones.length === 0 && (
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busy}
                        onClick={() => setRemovingDesk(desk)}
                      >
                        {t('deskPhones.removeDesk')}
                      </Button>
                    )}
                  </span>
                </Td>
              </Tr>

              {desk.phones.length === 0 && (
                <Tr>
                  <Td wrap>
                    <span className="pl-5 text-ink-3">{t('deskPhones.noPhones')}</span>
                  </Td>
                  <Td />
                  <Td />
                  <Td />
                  <Td />
                </Tr>
              )}

              {desk.phones.map((phone, phoneIndex) => (
                <Tr key={phone.id}>
                  <Td>
                    <span className="pl-5">{phone.name}</span>
                  </Td>
                  <Td>
                    <span className="tabular-nums">{phone.extension}</span>
                  </Td>
                  <Td wrap>
                    {desks.length > 1 && (
                      <Select
                        aria-label={t('deskPhones.moveTo')}
                        value={desk.id}
                        disabled={busy}
                        onChange={(e) =>
                          void act(async () => {
                            await deskPhonesApi.move(phone.id, e.target.value);
                            await load();
                          })
                        }
                      >
                        {desks.map((other) => (
                          <option key={other.id} value={other.id}>
                            {other.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Td>
                  <Td wrap>
                    {/* Whether it sets itself up goes under whether it is connected, rather
                        than in a column of its own: a desk's rows need the width for its
                        actions, and most phones have nothing to say there. */}
                    <span className="flex flex-col items-start gap-0.5">
                      <Pill tone={phone.reachable ? 'ok' : 'neutral'} dot>
                        {phone.reachable ? t('deskPhones.reachable') : t('deskPhones.unreachable')}
                      </Pill>
                      {phone.macAddress !== null && (
                        <span className="text-secondary text-ink-3">
                          <span className="tabular-nums">{pairs(phone.macAddress)}</span> ·{' '}
                          {phone.provisionedAt === null
                            ? t('deskPhones.notFetchedYet')
                            : t('deskPhones.fetchedAt', {
                                when: new Date(phone.provisionedAt).toLocaleString(),
                              })}
                        </span>
                      )}
                    </span>
                  </Td>
                  <Td>
                    <span className="flex justify-end gap-1.5">
                      <Order
                        index={phoneIndex}
                        count={desk.phones.length}
                        disabled={busy}
                        onMove={(by) =>
                          swap(desk.phones, phoneIndex, by, (ids) =>
                            deskPhonesApi.reorderPhones(desk.id, ids),
                          )
                        }
                      />
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => openSetup(phone, setSetup, setMac, setAddress)}
                      >
                        {t('deskPhones.setUp')}
                      </Button>
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          void act(async () => setShown(await deskPhonesApi.resetPassword(phone.id)))
                        }
                      >
                        {t('deskPhones.newPassword')}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busy}
                        onClick={() => setRemoving(phone)}
                      >
                        {t('deskPhones.remove')}
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          ))}
        </Table>
      )}

      <Dialog
        open={naming !== null}
        title={
          naming === null
            ? ''
            : naming.kind === 'desk'
              ? t('deskPhones.addDesk')
              : naming.kind === 'phone'
                ? t('deskPhones.addPhoneTitle', { desk: naming.desk.name })
                : t('deskPhones.renameDeskTitle', { name: naming.desk.name })
        }
        onClose={() => setNaming(null)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setNaming(null)}>
              {t('fleet.cancel')}
            </Button>
            <Button variant="primary" disabled={busy || name.trim() === ''} onClick={submitName}>
              {naming?.kind === 'rename'
                ? t('deskPhones.renameDesk')
                : naming?.kind === 'phone'
                  ? t('deskPhones.addPhone')
                  : t('deskPhones.addDesk')}
            </Button>
          </>
        }
      >
        <Field
          label={naming?.kind === 'phone' ? t('deskPhones.name') : t('deskPhones.desk')}
          help={
            naming?.kind === 'phone'
              ? t('deskPhones.phoneNameHelp', { desk: naming.desk.name })
              : naming?.kind === 'desk'
                ? `${t('deskPhones.nameHelp')} ${t('deskPhones.addDeskHelp')}`
                : t('deskPhones.nameHelp')
          }
        >
          <Input
            ref={nameBox}
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              // Enter adds rather than submitting the settings page around it.
              e.preventDefault();
              submitName();
            }}
          />
        </Field>
      </Dialog>

      {/* Shown once. The password is on the PBX and nowhere else, so this dialog is the one
          chance to read it; a new one is a click away if it is lost. */}
      <Dialog
        open={shown !== null}
        title={t('deskPhones.typeIntoIt', { name: shown?.name ?? '' })}
        onClose={() => setShown(null)}
        actions={
          <Button variant="primary" onClick={() => setShown(null)}>
            {t('deskPhones.typedIt')}
          </Button>
        }
      >
        {shown !== null && (
          <div className="flex flex-col gap-3">
            <Banner tone="warn">{t('deskPhones.shownOnce')}</Banner>
            <KeyValue>
              <KeyValueRow label={t('deskPhones.server')}>
                <span className="tabular-nums select-all">
                  {shown.server === '' ? t('deskPhones.serverUnset') : shown.server}
                </span>
              </KeyValueRow>
              <KeyValueRow label={t('deskPhones.userName')}>
                <span className="tabular-nums select-all">{shown.extension}</span>
              </KeyValueRow>
              <KeyValueRow label={t('deskPhones.password')}>
                <span className="font-mono select-all">{shown.password}</span>
              </KeyValueRow>
            </KeyValue>
            <p className="m-0 text-secondary text-ink-3">{t('deskPhones.whereToType')}</p>
          </div>
        )}
      </Dialog>

      {/* Self-setup: the MAC from the label, then the one address to type into the phone.
          Saving the MAC is what mints the firm's address the first time. */}
      <Dialog
        open={setup !== null}
        wide
        title={t('deskPhones.setUpTitle', { name: setup?.name ?? '' })}
        onClose={() => {
          setSetup(null);
          setSynced(null);
        }}
        actions={
          <>
            {setup?.macAddress != null && (
              <Button
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    if (setup === null) return;
                    await deskPhonesApi.checkSync(setup.id);
                    setSynced(t('deskPhones.sent'));
                  })
                }
              >
                {t('deskPhones.sendSettings')}
              </Button>
            )}
            <Button
              variant="primary"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  if (setup === null) return;
                  const saved = await deskPhonesApi.setMac(
                    setup.id,
                    mac.trim() === '' ? null : mac.trim(),
                  );
                  setSetup(saved.phone);
                  setAddress(saved.url);
                  await load();
                })
              }
            >
              {t('settings.save')}
            </Button>
          </>
        }
      >
        {setup !== null && (
          <div className="flex flex-col gap-3">
            {error !== null && <Banner tone="danger">{error}</Banner>}
            {synced !== null && <Banner tone="ok">{synced}</Banner>}

            <Field label={t('deskPhones.mac')} help={t('deskPhones.macHelp')}>
              <Input
                value={mac}
                placeholder="80:5E:C0:12:34:56"
                onChange={(e) => setMac(e.target.value)}
                onKeyDown={(e) => {
                  // Enter saves the MAC rather than submitting the settings page around it.
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
            </Field>

            {setup.macAddress !== null &&
              (address === null ? (
                <Banner tone="warn">{t('deskPhones.noAddress')}</Banner>
              ) : (
                // Label above rather than beside: a URL wants the dialog's whole width, and
                // a key column would wrap the label into two lines beside it.
                <Field label={t('deskPhones.address')} help={t('deskPhones.whereToTypeAddress')}>
                  <span className="font-mono select-all break-all">{address}</span>
                </Field>
              ))}
          </div>
        )}
      </Dialog>

      <Dialog
        open={removing !== null}
        title={t('deskPhones.remove')}
        onClose={() => setRemoving(null)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setRemoving(null)}>
              {t('fleet.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={busy || removing === null}
              onClick={() =>
                void act(async () => {
                  if (removing === null) return;
                  await deskPhonesApi.remove(removing.id);
                  setRemoving(null);
                  await load();
                })
              }
            >
              {t('deskPhones.remove')}
            </Button>
          </>
        }
      >
        <p className="m-0">
          {t('deskPhones.removeConfirm', {
            name: removing?.name ?? '',
            extension: removing?.extension ?? '',
          })}
        </p>
      </Dialog>

      <Dialog
        open={removingDesk !== null}
        title={t('deskPhones.removeDesk')}
        onClose={() => setRemovingDesk(null)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setRemovingDesk(null)}>
              {t('fleet.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={busy || removingDesk === null}
              onClick={() =>
                void act(async () => {
                  if (removingDesk === null) return;
                  await deskPhonesApi.removeDesk(removingDesk.id);
                  setRemovingDesk(null);
                  await load();
                })
              }
            >
              {t('deskPhones.removeDesk')}
            </Button>
          </>
        }
      >
        <p className="m-0">
          {t('deskPhones.removeDeskConfirm', { name: removingDesk?.name ?? '' })}
        </p>
      </Dialog>
    </Section>
  );
}

/** What the name dialog is open for. */
type Naming =
  | { kind: 'desk' }
  | { kind: 'phone'; desk: Desk }
  | { kind: 'rename'; desk: Desk };

/** Up and down arrows for one item of an ordered list; none when there is only one. */
function Order({
  index,
  count,
  disabled,
  onMove,
}: {
  index: number;
  count: number;
  disabled: boolean;
  onMove: (by: -1 | 1) => void;
}) {
  const { t } = useTranslation();

  if (count < 2) return null;

  return (
    <>
      <Button
        size="sm"
        variant="quiet"
        aria-label={t('deskPhones.moveUp')}
        title={t('deskPhones.moveUp')}
        disabled={disabled || index === 0}
        onClick={() => onMove(-1)}
      >
        <Icon name="arrow-up" small />
      </Button>
      <Button
        size="sm"
        variant="quiet"
        aria-label={t('deskPhones.moveDown')}
        title={t('deskPhones.moveDown')}
        disabled={disabled || index === count - 1}
        onClick={() => onMove(1)}
      >
        <Icon name="arrow-down" small />
      </Button>
    </>
  );
}
